import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Settings2, CalendarDays, ListChecks, Wallet, Users, Armchair,
  Heart, Store, Building2, UtensilsCrossed, Clock, Luggage, Palette, Images,
  Music2, Gift, Plane, Contact, Settings, HelpCircle, Menu, X, Search, LogOut,
  Sun, Moon, ShieldCheck, Printer, ArrowLeftRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWedding } from "@/context/WeddingContext";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { section: "Plan", items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/setup", label: "Setup", icon: Settings2 },
    { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/app/timeline", label: "Timeline", icon: ListChecks },
  ]},
  { section: "Details", items: [
    { to: "/app/budget", label: "Budget", icon: Wallet },
    { to: "/app/guests", label: "Guest List", icon: Users },
    { to: "/app/seating", label: "Seating Plan", icon: Armchair },
    { to: "/app/party", label: "Wedding Party", icon: Heart },
    { to: "/app/vendors", label: "Vendors", icon: Store },
    { to: "/app/venues", label: "Venue Comparison", icon: Building2 },
    { to: "/app/food", label: "Food & Drinks", icon: UtensilsCrossed },
  ]},
  { section: "Wedding Day", items: [
    { to: "/app/itinerary", label: "Itinerary", icon: Clock },
    { to: "/app/packing", label: "Packing", icon: Luggage },
    { to: "/app/theme", label: "Theme Board", icon: Palette },
    { to: "/app/gallery", label: "Photo Gallery", icon: Images },
    { to: "/app/music", label: "Music", icon: Music2 },
  ]},
  { section: "After", items: [
    { to: "/app/gifts", label: "Gifts & Thank-Yous", icon: Gift },
    { to: "/app/honeymoon", label: "Honeymoon", icon: Plane },
    { to: "/app/contacts", label: "Contacts", icon: Contact },
  ]},
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("pmw_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("pmw_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

function NavItems({ onNavigate, isAdmin, base = "/app", demo = false }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-6">
      {NAV.map((group) => (
        <div key={group.section}>
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{group.section}</div>
          <div className="space-y-0.5">
            {group.items.map((it) => (
              <NavLink
                key={it.to}
                to={base + it.to.slice(4)}
                end={it.end}
                onClick={onNavigate}
                data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all duration-200 ${
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary hover:translate-x-1"
                  }`
                }
              >
                <it.icon className="h-[18px] w-[18px] transition-transform group-hover:scale-110" strokeWidth={1.5} />
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
      <div>
        <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">System</div>
        <div className="space-y-0.5">
          {!demo && (
          <NavLink to="/planners" onClick={onNavigate} data-testid="nav-my-planners"
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
            <ArrowLeftRight className="h-[18px] w-[18px]" strokeWidth={1.5} /> My Planners
          </NavLink>)}
          <NavLink to={base + "/settings"} onClick={onNavigate} data-testid="nav-settings"
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
            <Settings className="h-[18px] w-[18px]" strokeWidth={1.5} /> Settings
          </NavLink>
          <NavLink to={base + "/help"} onClick={onNavigate} data-testid="nav-help"
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
            <HelpCircle className="h-[18px] w-[18px]" strokeWidth={1.5} /> Help
          </NavLink>
          {isAdmin && !demo && (
            <NavLink to="/app/admin" onClick={onNavigate} data-testid="nav-admin"
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
              <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={1.5} /> Admin
            </NavLink>
          )}
        </div>
      </div>
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-6">
      <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
        <Heart className="h-4 w-4 text-primary-foreground" strokeWidth={2} fill="currentColor" />
      </div>
      <div className="leading-none">
        <div className="text-lg tracking-tight text-primary font-serif font-semibold">PlanMyWedding</div>
      </div>
    </div>
  );
}

export default function AppLayout({ children, base = "/app", demo = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dark, setDark] = useTheme();
  const { user, logout } = useAuth();
  const { wedding, saveStatus } = useWedding();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const doLogout = async () => { navigate("/", { replace: true }); await logout(); };
  const coupleLabel = wedding ? `${wedding.partner1 || "Partner 1"} & ${wedding.partner2 || "Partner 2"}` : "Your wedding";

  return (
    <div className="min-h-screen flex bg-background grain">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-secondary/30 sticky top-0 h-screen no-print z-10">
        <Brand />
        <NavItems isAdmin={isAdmin} base={base} demo={demo} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden no-print">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-background border-r border-border flex flex-col">
            <div className="flex items-center justify-between pr-3">
              <Brand />
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} data-testid="close-drawer"><X className="h-5 w-5" /></Button>
            </div>
            <NavItems isAdmin={isAdmin} base={base} demo={demo} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col relative z-10">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-8 h-16 border-b border-border bg-background/80 backdrop-blur-xl no-print">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="open-drawer"><Menu className="h-5 w-5" /></Button>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="open-search"
            className="flex items-center gap-2 text-sm text-muted-foreground rounded-full border border-border bg-secondary/50 px-4 py-2 hover:bg-secondary transition-colors w-full max-w-xs"
          >
            <Search className="h-4 w-4" /> Search guests, vendors, tasks…
          </button>
          <div className="ml-auto flex items-center gap-2">
            {!demo && <button onClick={() => navigate("/planners")} data-testid="wedding-switch-planner" title="Switch planner" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground rounded-full border border-border px-3 py-1.5 hover:bg-secondary transition-colors">
              <ArrowLeftRight className="h-3.5 w-3.5" /> Switch planner
            </button>}
            {!demo && <SaveBadgeInline status={saveStatus} />}
            <Button variant="ghost" size="icon" onClick={() => window.print()} data-testid="print-btn" title="Print"><Printer className="h-[18px] w-[18px]" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" title="Theme">
              {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            <div className="hidden sm:block text-right leading-tight mr-1">
              <div className="text-xs font-medium text-primary max-w-[140px] truncate">{coupleLabel}</div>
              <div className="text-[11px] text-muted-foreground max-w-[140px] truncate">{user?.email}</div>
            </div>
            {!demo && <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn" title="Log out"><LogOut className="h-[18px] w-[18px]" /></Button>}
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-8 max-w-[1400px] w-full print-full">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}

function SaveBadgeInline({ status }) {
  if (status === "saving") return <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="topbar-save">Saving…</span>;
  if (status === "saved") return <span className="text-xs text-primary hidden sm:inline" data-testid="topbar-save">Saved ✓</span>;
  if (status === "error") return <span className="text-xs text-destructive hidden sm:inline" data-testid="topbar-save">Couldn't save</span>;
  return null;
}
